import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  const [language, setLanguage] = useState('en');
  const { i18n: i18nInstance } = useTranslation();

  // Load theme and language from localStorage on mount
  useEffect(() => {
    try {
      const savedTheme = localStorage.getItem('theme');
      const savedLanguage = localStorage.getItem('language');
      
      if (savedTheme && ['light', 'dark'].includes(savedTheme)) {
        setTheme(savedTheme);
      } else {
        // Check system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setTheme(prefersDark ? 'dark' : 'light');
      }
      
      if (savedLanguage && ['en', 'ar', 'tr'].includes(savedLanguage)) {
        setLanguage(savedLanguage);
        // Update i18next language
        i18nInstance.changeLanguage(savedLanguage);
      } else {
        // Get language from i18next or default to 'en'
        const currentLang = i18nInstance.language || 'en';
        const supportedLang = ['en', 'ar', 'tr'].includes(currentLang) ? currentLang : 'en';
        setLanguage(supportedLang);
        i18nInstance.changeLanguage(supportedLang);
      }
    } catch (error) {
      console.error('Error loading theme/language:', error);
    }
  }, [i18nInstance]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Apply language to document (direction is handled by components)
  useEffect(() => {
    const root = document.documentElement;
    
    root.setAttribute('lang', language);
    
    // Don't set dir on root - let components handle their own direction
    // This prevents the sidebar from flipping sides
    
    localStorage.setItem('language', language);
    
    // Update i18next language when language changes
    if (i18nInstance.language !== language) {
      i18nInstance.changeLanguage(language);
    }
  }, [language, i18nInstance]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const toggleLanguage = () => {
    const langs = ['en', 'ar', 'tr'];
    const currentIndex = langs.indexOf(language) !== -1 ? langs.indexOf(language) : 0;
    const newLanguage = langs[(currentIndex + 1) % langs.length];
    setLanguage(newLanguage);
    i18nInstance.changeLanguage(newLanguage);
  };

  const value = {
    theme,
    language,
    setTheme,
    setLanguage,
    toggleTheme,
    toggleLanguage,
    isRTL: language === 'ar',
    isDark: theme === 'dark'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 